import { storyPhrases } from "../../constants/interventionStrings";
import {COPY_STRINGS} from '../../constants/translatedCopy';

/*
** This Library only keeps functions that are specifically used inside of
** ReaderManager.
*/

//insert commas between each word to cause the speech synthesizer to pause
export function getTextWithPauses(text, config, errors) {
  if (config.slowWholePhrase) {
    let textWithPauses = text;
    textWithPauses = textWithPauses.replace(/["]+/g, '');

    if (config.separateByComma) {
      textWithPauses = textWithPauses.replace(".","");
      return textWithPauses.split(' ').join(", ");
    } else if(config.separateByBreakTag) {
      textWithPauses = textWithPauses.replace(".","");
      return textWithPauses.split(' ').join(`<break time=\"${config.breakTime || 0.20}s\"/>`);
    } else if(config.separateByProsodyTag) {
      return `<prosody rate="${config.prosodyRate || "slow"}">${text}</prosody>`;
    }

    return textWithPauses;
  } else {
    // note: this code with put double breaks between two errors. This is expected behavior
    return text.replace(/["]+/g, '').split(' ').map((word, wordIndex) => {
      if (errors[wordIndex]) {
        if (config.separateByComma) {
          return ", " + word + ", ";
        } else if(config.separateByBreakTag) {
          return `<break time=\"${config.breakTime || 0.20}s\"/>${word}<break time=\"${config.breakTime || 0.20}s\"/>`;
        } else {
          return `<prosody rate="${config.prosodyRate || "slow"}">${word}</prosody>`;
        }
      } else {
        return word;
      }
    }).join(' ');
  }
}
// calculate the reader's WPM (Words Per Minute) score for this story
// based on phraseTimers
export function calculateWPM(phraseTimes){
  let totalWords = 0;
  let totalMSElapsed = 0;
  if(!phraseTimes || !Array.isArray(phraseTimes) || !phraseTimes.length || phraseTimes.length <= 0){
    return 68;
  }
  let timers = phraseTimes.flat();
  for(let i = 0; i < timers.length; i++){
    let timer = timers[i] || {};
    if(timer.start && timer.end){
      totalMSElapsed += timer.end - timer.start;
      totalWords += timer.numWords;
    }
  }
  if(totalMSElapsed == 0){
    return 68;
  }
  return Math.round(totalWords / (totalMSElapsed / 60000));
}

export function calculateMostRecentScores(errors, totalMSElapsed){
  const storyErrors = formatStoryErrorsForScoring(errors);
  let accuracy = 100;
  if (storyErrors && storyErrors.length > 0 && storyErrors[0]) {
    accuracy = calculateAccuracy([storyErrors]);
  }
  
  let wcpm = undefined;
  if (totalMSElapsed) {
    wcpm = calculateWCPM(storyErrors,totalMSElapsed);
  }

  let errorCount = 0;
  for (let i = 0; i < storyErrors.length; i++) {
    let phraseErrors = storyErrors[i];
    if (!phraseErrors) continue;

    for (let j = 0; j < phraseErrors.length; j++){
      if (phraseErrors[j]){
        errorCount += 1;
      }
    }
  }

  const errorRate = Math.round((1 - accuracy / 100) * 10000) / 10000; // Round the result to a maximum of four decimal places

  return { storyErrors, accuracy, wcpm, errorCount, errorRate };
}

export function calculateWCPM(errors, totalMSElapsed){
  if(!Array.isArray(errors) || totalMSElapsed <= 0 || errors.length <= 0){
    return 0;
  }
  let totalCorrect = errors.flat(4).reduce(((runningTotal, error) => error ? runningTotal : runningTotal + 1), 0);
  return Math.round(totalCorrect / (totalMSElapsed / 60000));
}

// calculate the reader's accuracy based on totalErrors
// and returns a percentage based number
export function calculateAccuracy(totalErrors){
  totalErrors = Array.isArray(totalErrors) ? totalErrors : [];
  let totalWords = 0;
  let totalCorrect = 0;
  for(let i = 0; i < totalErrors.length; i++){
    let pageErrors = totalErrors[i];
    if(!pageErrors) continue // OR should it push an empty array or something else?
    for(let j = 0; j < pageErrors.length; j++){
      let phraseErrors = pageErrors[j];
      if(!phraseErrors) continue // OR should it push an empty array or something else?
      for(let k = 0; k < phraseErrors.length; k++){
        totalWords += 1;
        if(!phraseErrors[k]){
          totalCorrect += 1;
        }
      }
    }
  }
  if(totalWords <= 0){
    return 0;
  }
  return Math.floor((totalCorrect/totalWords)*100);
}

export function formatStoryErrorsForScoring(errors){
  let storyErrors = [];
  errors.forEach((page)=>{
    page.forEach((phrase)=>{
      storyErrors.push(phrase.totalErrors);
    });
  });
  return storyErrors;
}

export function getNonFlaggedErrors(errors,flaggedPhrases){
  let adjustedErrors = errors[0].filter((error,index)=>{
    if(flaggedPhrases[index]){
      return !flaggedPhrases[index].triggered;
    }else{
      return true;
    }
  });

  return [adjustedErrors];
}

export function calculateAccuracyFromScoredTranscript(scoredTranscript){
  let totalErrors = scoredTranscript.map((page) => page.map((phraseScore) => phraseScore.totalErrors));
  return calculateAccuracy(totalErrors);
}

// generate the string to be spoken by Lexa telling the reader their story options
// this is done here instead of inside story picker so that we can preload the audio
export function speakChooseStoryMessage(speech, recommendedStories, onEnd, enableButtons, returnToListening, readAloud=false){
  if(recommendedStories.length > 1){
    speech.speak({textToSpeak: COPY_STRINGS.ive_selected_x_stories(recommendedStories.length), onStart: enableButtons, onEnd: enableButtons});
    for(let i = 0; i < recommendedStories.length; i++){
      let story = recommendedStories[i];
      let titleSpeechProps = {textToSpeak: `"${story.title}"`, skipTranslation: true};
      let last = false;
      if(i == recommendedStories.length - 1){
        titleSpeechProps.textToSpeak = `${COPY_STRINGS.and} ${titleSpeechProps.textToSpeak}`
        titleSpeechProps.onEnd = onEnd;
        last = true;
      }
      speech.speak(titleSpeechProps, last, undefined, true);
    }
  } else {
    if(recommendedStories.length === 1){
      speech.speak({textToSpeak: COPY_STRINGS.id_like_you_to_read, onStart: enableButtons, onEnd: enableButtons});
      speech.speak({textToSpeak: recommendedStories[0].title, skipTranslation: true}, undefined, undefined, true);
      speech.speak({textToSpeak: COPY_STRINGS.click_story_to_start, onEnd}, returnToListening);
    } else {
      speech.speak({textToSpeak: COPY_STRINGS.no_stories_speech, onStart: enableButtons, onEnd}, returnToListening);
    }
  }
}